function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
import "core-js/modules/es.array.reverse.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { computed, observable, runInAction, untracked, makeObservable } from "mobx";
import DeveloperError from "terriajs-cesium/Source/Core/DeveloperError";
import AsyncLoader from "../Core/AsyncLoader";
import Result from "../Core/Result";
import TerriaError from "../Core/TerriaError";
import { getName } from "./CatalogMemberMixin";
import { applyItemProperties } from "./GroupMixin";
/**
 * A mixin for a Model that acts as a "reference" to another Model, which is its "true"
 * representation. The reference is "dereferenced" to obtain the other model, but only
 * after an optional asynchronous operation is completed. For example, a `CkanCatalogItem`
 * acts as a reference to another type of catalog item. Once the CKAN dataset record is
 * loaded, the `CkanCatalogItem` may be dereferenced to obtain the `WebMapServiceCatalogItem`,
 * `GeoJsonCatalogItem`, or whatever else representing the dataset.
 */
function ReferenceMixin(Base) {
  var _class, _descriptor;
  let ReferenceMixinClass = (_class = class ReferenceMixinClass extends Base {
    constructor() {
      super(...arguments);
      /** A "weak" reference has a target which doesn't include the `sourceReference` property.
       * This means the reference is treated more like a shortcut to the target. So share links, for example, will use the target instead of sourceReference. */
      _defineProperty(this, "weakReference", false);
      _initializerDefineProperty(this, "_target", _descriptor, this);
      _defineProperty(this, "_referenceLoader", new AsyncLoader(async () => {
        const previousTarget = untracked(() => this._target);
        const target = await this.forceLoadReference(previousTarget);
        if (!target) {
          throw new DeveloperError("Failed to create reference");
        }
        if ((target === null || target === void 0 ? void 0 : target.uniqueId) !== this.uniqueId) {
          throw new DeveloperError("The model returned by `forceLoadReference` must be constructed with its `uniqueId` set to the same value as the Reference model.");
        }
        if (!this.weakReference && (target === null || target === void 0 ? void 0 : target.sourceReference) !== this) {
          throw new DeveloperError("The model returned by `forceLoadReference` must be constructed with its `sourceReference` set to the Reference model.");
        }
        if (this.weakReference && target !== null && target !== void 0 && target.sourceReference) {
          throw new DeveloperError('This is a "weak" reference, so the model returned by `forceLoadReference` must not have a `sourceReference` set.');
        }
        runInAction(() => {
          this._target = target;
        });
      }));
      makeObservable(this);
    }
    get loadReferenceResult() {
      return this._referenceLoader.result;
    }

    /**
     * Gets a value indicating whether the reference is currently loading. While this is true,
     * {@link ModelMixin#target} may be undefined or stale.
     */
    get isLoadingReference() {
      return this._referenceLoader.isLoading;
    }

    /**
     * Gets the target model of the reference. This model must have the same `id` as this model.
     */
    get target() {
      return this._target;
    }

    /**
     * If this a nested reference return the target of the final reference.
     */
    get nestedTarget() {
      return ReferenceMixin.isMixedInto(this._target) ? this._target.nestedTarget : this._target;
    }

    /**
     * Asynchronously loads the reference. When the returned promise resolves,
     * {@link ReferenceMixin#target} should return the target of the reference.
     * @param forceReload True to force the load to happen again, even if nothing
     *        appears to have changed since the last time it was loaded.
     *
     * This returns a Result object, it will contain errors if they occur - they will not be thrown.
     * To throw errors, use `(await loadMetadata()).throwIfError()`
     *
     * {@see AsyncLoader}
     */
    async loadReference() {
      let forceReload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      const result = (await this._referenceLoader.load(forceReload)).clone("Failed to load reference `".concat(getName(this), "`"));
      if (!result.error && this.target) {
        runInAction(() => {
          // Copy knownContainerUniqueIds to target
          this.knownContainerUniqueIds.forEach(id => !this.target.knownContainerUniqueIds.includes(id) ? this.target.knownContainerUniqueIds.push(id) : null);
        });
        applyItemProperties(this, this.target);
      }
      return result;
    }

    /**
     * Recursively load a nested chain of references till the given maxDepth.
     *
     * If this reference points to another reference and so on.. then this
     * method will load them all up to the given depth.
     *
     * @param maxDepth The maximum depth up to which the references should be resolved.
     * @returns A promise that is fulfilled with a Result value when all the references have been loaded.
     */
    async recursivelyLoadReference(maxDepth) {
      let currentTarget = this;
      const errors = [];
      while (maxDepth > 0 && ReferenceMixin.isMixedInto(currentTarget)) {
        (await currentTarget.loadReference()).pushErrorTo(errors);
        currentTarget = currentTarget.target;
        maxDepth -= 1;
      }
      const maybeError = TerriaError.combine(errors, "Failed to recursively load reference `${this.uniqueId}`");
      return Result.none(maybeError);
    }

    /**
     * Forces load of the reference. This method does _not_ need to consider
     * whether the reference is already loaded.
     *
     * You **can not** make changes to observables until **after** an asynchronous call {@see AsyncLoader}.
     *
     * Errors can be thrown here.
     *
     * {@see AsyncLoader}
     */

    dispose() {
      super.dispose();
      this._referenceLoader.dispose();
    }
  }, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_target", [observable], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _applyDecoratedDescriptor(_class.prototype, "nestedTarget", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "nestedTarget"), _class.prototype), _class);
  return ReferenceMixinClass;
}
(function (_ReferenceMixin) {
  function isMixedInto(model) {
    return model && "loadReference" in model;
  }
  _ReferenceMixin.isMixedInto = isMixedInto;
})(ReferenceMixin || (ReferenceMixin = {}));
export default ReferenceMixin;